//reports-page.tsx

import { useState, useContext, useEffect, Fragment } from "react";
import Page from "../components/page";
import { Formik, Form, FastField, FieldProps, Field } from "formik";
import { AppTextField } from "../components/text-field";
import {
  MenuItem,
  Box,
  Button,
  Typography,
  TableCell,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import * as yup from "yup";
import { validationMessages } from "../utils/validation/messages";
import {
  reportsApi,
  CropProductionReportAdvanced,
  CropProductionReportSimple,
  PackagingReportSimple,
  PackagingReportAdvanced,
  SalesReportData,
  OrdersSummaryReport,
} from "../services/api/reports";
import { DateTime } from "luxon";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext, ErrorContext } from "../components/app-routes";
import { formatCurrency } from "./currency";
import mixpanel from "mixpanel-browser";
import { UpgradePlanDialog } from "../components/upgrade-plan-dialog";
import { useHistory } from "react-router-dom";
import { unitSystemLabel } from "./crops-page";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { GridRowClassNameParams, GridColDef } from "@mui/x-data-grid";
import { StripedDataGrid } from "../components/striped-data-grid";
import { v4 } from "uuid";
import { Size } from "../models/size";
import { sizesApi } from "../services/api/sizes";
import { getCustomLabel } from "../utils/custom-labels";

interface CropProductionRow {
  // numbers
  HarvestedBufferExpected?: number;

  // seed lots (numbers)
  SeedLots?: string;
  SoakedSeedLotNumbers?: string;
  SowedSeedLotNumbers?: string;

  // seed lot notes
  SeedLotNotes?: string;
  SoakedSeedLotNotes?: string;
  SowedSeedLotNotes?: string;
}

interface FormValues {
  report: string;
  from: DateTime | null;
  to: DateTime | null;
}

const validationSchema = yup.object({
  report: yup.string().required(validationMessages.Required),
  from: yup
    .date()
    .typeError(validationMessages.Required)
    .required(validationMessages.Required),
  to: yup
    .date()
    .typeError(validationMessages.Required)
    .required(validationMessages.Required),
});

const border = "2px solid black";

export default function ReportsPage() {
  const [busy, setBusy] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string>("sales");
  const [ordersSummaryReport, setOrdersSummaryReport] = useState<
    OrdersSummaryReport | undefined
  >(undefined);
  const [sizes, setSizes] = useState<Size[] | undefined>(undefined);
  const [salesReport, setSalesReport] = useState<SalesReportData[] | undefined>(
    undefined,
  );
  const [cropProductionReportAdvanced, setCropProductionReportAdvanced] =
    useState<CropProductionReportAdvanced | undefined>(undefined);
  const [cropProductionReportSimple, setCropProductionReportSimple] = useState<
    CropProductionReportSimple | undefined
  >(undefined);
  const [packagingReportSimple, setPackagingReportSimple] = useState<
    PackagingReportSimple | undefined
  >();
  const [packagingReportAdvanced, setPackagingReportAdvanced] = useState<
    PackagingReportAdvanced | undefined
  >();
  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);
  const { grower } = useContext(UserContext);
  const countryISO = grower?.CountryISO;
  const { handleError } = useContext(ErrorContext);
  const history = useHistory();

  if (!grower || !countryISO) {
    throw new Error("Invalid grower");
  }

  const fmtTrunc = (decimals: number) => (value: unknown) => {
    if (value === null || value === undefined || value === "") return "";
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(n)) return String(value);

    const factor = Math.pow(10, decimals);

    // truncate (not round). for negatives, use ceil to truncate toward 0.
    const t =
      n >= 0 ? Math.floor(n * factor) / factor : Math.ceil(n * factor) / factor;

    return t.toFixed(decimals);
  };

  const fmtTrunc1 = fmtTrunc(1);

  const salesReportColumns: GridColDef[] = [
    {
      field: "OrderNumber",
      headerName: "Order Number",
      width: 200,
    },
    {
      field: "HarvestDateFormatted",
      headerName: "Harvest Date",
      width: 200,
    },

    {
      field: "CustomerName",
      headerName: "Customer",
      width: 200,
    },
    {
      field: "CustomerType",
      headerName: "Customer Type",
      width: 200,
    },
    {
      field: "ProductName",
      headerName: "Product",
      width: 200,
    },
    {
      field: "SizeName",
      headerName: "Size",
      width: 200,
    },
    {
      field: "PriceFormatted",
      headerName: "Price",
      width: 200,
    },
    {
      field: "Quantity",
      headerName: "Quantity",
      width: 200,
    },
    {
      field: "TotalFormatted",
      headerName: "Total",
      width: 200,
    },
    {
      field: "Seeds",
      headerName: "Seeds",
      width: 200,
    },
    {
      field: "SeedLots",
      headerName: "Seed Lots",
      width: 200,
    },
  ];

  const packagingReportSimpleColumns: GridColDef[] = [
    { field: "Product", headerName: "Product Name", width: 200 },
    { field: "Size", headerName: "Product Size", width: 200 },
    {
      field: "Weight",
      headerName: `Product Weight (${unitSystemLabel(grower)})`,
      width: 200,
    },
    { field: "Quantity", headerName: "Quantity", width: 200 },

    // Pre-formatted string
    { field: "HarvestDateFormatted", headerName: "Harvest Date", width: 200 },

    { field: "Orders", headerName: "Orders Affected", width: 200 },
  ];

  const packagingReportAdvancedColumns: GridColDef[] = [
    { field: "Product", headerName: "Product Name", width: 200 },
    { field: "Orders", headerName: "Orders Affected", width: 200 },
    { field: "Size", headerName: "Product Size", width: 200 },
    {
      field: "Weight",
      headerName: `Product Weight (${unitSystemLabel(grower)})`,
      width: 200,
    },
    { field: "QuantityExpected", headerName: "Expected Qty", width: 200 },
    { field: "QuantityActual", headerName: "Actual Qty", width: 200 },

    // Preformatted dates
    {
      field: "DateExpectedFormatted",
      headerName: "Exp. Pack. Date",
      width: 200,
    },
    { field: "DateActualFormatted", headerName: "Act. Pack. Date", width: 200 },
    {
      field: "HarvestDateFormatted",
      headerName: "Exp. Harvest Date",
      width: 200,
    },

    { field: "CompletedBy", headerName: "Completed By", width: 200 },
    { field: "Notes", headerName: "Notes", width: 200 },
  ];

  const cropProductionReportSimpleColumns: GridColDef[] = [
    { field: "BatchNumber", headerName: "Batch", width: 120 },

    {
      field: "SowingDateFormatted",
      headerName: "Sowing Date",
      width: 200,
    },

    { field: "Crop", headerName: "Crop", width: 200 },
    {
      field: "SowingRate",
      headerName: `Sowing Rate per Tray (${unitSystemLabel(grower)})`,
      width: 200,
    },
    { field: "TraysSowed", headerName: "Trays Sowed", width: 200 },
    {
      field: "ExpectedYield",
      headerName: `Expected Yield per Tray (${unitSystemLabel(grower)})`,
      width: 200,
    },
    {
      field: "TotalSeedWeight",
      headerName: `Total Seed Weight for Sowing (${unitSystemLabel(grower)})`,
      width: 200,
    },

    {
      field: "HarvestDateFormatted",
      headerName: "For Harvest On",
      width: 200,
    },

    { field: "Orders", headerName: "Orders Affected", width: 200 },
  ];

  const rowFromGetterArgs = (args: any[]) => {
    if (args.length >= 2) return args[1]; // (value, row)
    const p = args[0]; // (params)
    return p && typeof p === "object" ? p.row : undefined;
  };

  const cropProductionReportAdvancedColumns: GridColDef[] = [
    { field: "BatchNumber", headerName: "Batch", width: 120 },
    { field: "SowedCropName", headerName: "Crop Name", width: 200 },
    { field: "SowedSeedName", headerName: "Seed Name", width: 200 },
    { field: "SowedSeedVariety", headerName: "Seed Variety", width: 200 },
    {
      field: "SeedLots",
      headerName: "Seed Lots",
      width: 200,
      valueGetter: (...args: any[]) => {
        const row = rowFromGetterArgs(args) as CropProductionRow | undefined;
        if (!row) return "";
        return (
          row.SoakedSeedLotNumbers ||
          row.SowedSeedLotNumbers ||
          row.SeedLots ||
          ""
        );
      },
    },
    {
      field: "SowedDateActualFormatted",
      headerName: "Actual Sow Date",
      width: 200,
    },
    {
      field: "SowedDateExpectedFormatted",
      headerName: "Expected Sow Date",
      width: 200,
    },
    {
      field: "HarvestedDateExpectedFormatted",
      headerName: "Expected Harvest Date",
      width: 200,
    },
    {
      field: "HarvestedDateActualFormatted",
      headerName: "Actual Harvest Date",
      width: 200,
    },

    { field: "Orders", headerName: "Orders Affected", width: 200 },

    {
      field: "SoakedDateExpectedFormatted",
      headerName: "Expected Soak Date",
      width: 200,
    },
    {
      field: "SoakedDateActualFormatted",
      headerName: "Actual Soak Date",
      width: 200,
    },

    { field: "SoakedBy", headerName: "Soaked By", width: 200 },
    { field: "SoakedNotes", headerName: "Soak Notes", width: 200 },

    {
      field: "SoakedTraysExpected",
      headerName: "Expected Soaked Tray Count",
      width: 200,
    },
    {
      field: "SoakedTraysActual",
      headerName: "Actual Soaked Tray Count",
      width: 200,
    },

    { field: "SowedBy", headerName: "Sowed By", width: 200 },
    { field: "SowedNotes", headerName: "Sow Notes", width: 200 },

    {
      field: "SowedTraysNeeded",
      headerName: "Trays Needed To Fulfill Orders",
      width: 200,
      valueFormatter: fmtTrunc1,
    },
    {
      field: "SowedTraysBuffer",
      headerName: "Buffer Trays",
      width: 200,
      valueFormatter: fmtTrunc1,
    },

    {
      field: "SowedTraysExpected",
      headerName: "Expected Sow Tray Count",
      width: 200,
    },
    {
      field: "SowedTraysActual",
      headerName: "Actual Sow Tray Count",
      width: 200,
    },

    {
      field: "SowedCropSowingRate",
      headerName: `Sowing Rate (${unitSystemLabel(grower)})`,
      width: 200,
    },
    {
      field: "SowedCropExpectedYield",
      headerName: `Expected Yield per Tray (${unitSystemLabel(grower)})`,
      width: 200,
    },
    { field: "SowedCropNotes", headerName: "Crop Notes", width: 200 },
    {
      field: "SowedCropBufferPercentage",
      headerName: "Crop Buffer Percentage",
      width: 200,
    },

    { field: "SowedSeedNotes", headerName: "Seed Notes", width: 200 },

    {
      field: "SeedLotNotes",
      headerName: "Seed Lot Notes",
      width: 200,
      valueGetter: (...args: any[]) => {
        const row = rowFromGetterArgs(args) as CropProductionRow | undefined;
        if (!row) return "";
        return (
          row.SoakedSeedLotNotes ||
          row.SowedSeedLotNotes ||
          row.SeedLotNotes ||
          ""
        );
      },
    },

    {
      field: "BlackoutDateExpectedFormatted",
      headerName: "Expected Blackout Date",
      width: 200,
    },
    {
      field: "BlackoutDateActualFormatted",
      headerName: "Actual Blackout Date",
      width: 200,
    },

    { field: "BlackoutBy", headerName: "Blackout By", width: 200 },
    { field: "BlackoutNotes", headerName: "Blackout Notes", width: 200 },
    {
      field: "BlackoutTraysExpected",
      headerName: "Expected Blackout Tray Count",
      width: 200,
    },
    {
      field: "BlackoutTraysActual",
      headerName: "Actual Blackout Tray Count",
      width: 200,
    },

    {
      field: "UncoveredDateExpectedFormatted",
      headerName: "Expected Uncover Date",
      width: 200,
    },
    {
      field: "UncoveredDateActualFormatted",
      headerName: "Actual Uncover Date",
      width: 200,
    },

    { field: "UncoveredBy", headerName: "Uncovered By", width: 200 },
    { field: "UncoveredNotes", headerName: "Uncover Notes", width: 200 },

    {
      field: "UncoveredTraysExpected",
      headerName: "Expected Uncover Tray Count",
      width: 200,
    },
    {
      field: "UncoveredTraysActual",
      headerName: "Actual Uncover Tray Count",
      width: 200,
    },

    { field: "HarvestedBy", headerName: "Harvested By", width: 200 },
    { field: "HarvestedNotes", headerName: "Harvest Notes", width: 200 },
    {
      field: "ExpectedCutHarvestTrayCount",
      headerName: "Expected Cut Harvest Tray Count",
      width: 200,
    },
    {
      field: "ActualCutHarvestTrayCount",
      headerName: "Actual Cut Harvest Tray Count",
      width: 200,
    },
    {
      field: "ExpectedLiveHarvestTrayCount",
      headerName: "Expected Live Harvest Tray Count",
      width: 200,
    },
    {
      field: "ActualLiveHarvestTrayCount",
      headerName: "Actual Live Harvest Tray Count",
      width: 200,
    },
    {
      field: "ExpectedCutHarvestYield",
      headerName: `Expected Cut Harvest Yield (${unitSystemLabel(grower)})`,
      width: 200,
    },
    {
      field: "ActualCutHarvestYield",
      headerName: `Actual Cut Harvest Yield (${unitSystemLabel(grower)})`,
      width: 200,
    },
    {
      field: "ExpectedCutHarvestBuffer",
      headerName: `Expected Cut Harvest Buffer (${unitSystemLabel(grower)})`,
      width: 200,
      valueFormatter: (value) => {
        const n = typeof value === "number" ? value : Number(value);
        return Number.isFinite(n) ? String(Math.round(n)) : "";
      },
    },
    {
      field: "ActualCutHarvestBuffer",
      headerName: `Actual Cut Harvest Buffer (${unitSystemLabel(grower)})`,
      width: 200,
      valueFormatter: (value) => {
        const n = typeof value === "number" ? value : Number(value);
        return Number.isFinite(n) ? String(Math.round(n)) : "";
      },
    },
  ];

  useEffect(() => {
    if (grower.Plan === "business" || grower.Plan === "pro") {
      return;
    }

    setOpenUpgradePlanDialog(true);
  }, []);

  const handleOnSubmit = async (values: FormValues) => {
    if (!values.from || !values.to) {
      throw new Error("Invalid state");
    }
    setSelectedReport(values.report);
    setBusy(true);
    setSalesReport(undefined);
    setCropProductionReportSimple(undefined);
    setCropProductionReportAdvanced(undefined);
    setPackagingReportAdvanced(undefined);
    setPackagingReportSimple(undefined);
    setOrdersSummaryReport(undefined);

    try {
      switch (values.report) {
        case "orders-summary":
          setOrdersSummaryReport(
            await reportsApi.getOrdersSummaryReport(values.from, values.to),
          );
          setSizes(await sizesApi.getAll());
          break;
        case "sales":
          const report = await reportsApi.getSalesReport(
            values.from,
            values.to,
          );

          setSalesReport(report);
          break;

        case "crop-production":
          const cropProductionReport = await reportsApi.getCropProductionReport(
            values.from,
            values.to,
          );

          if (grower.Workflow === "simple") {
            setCropProductionReportSimple(
              cropProductionReport as CropProductionReportSimple,
            );
          } else {
            setCropProductionReportAdvanced(
              cropProductionReport as CropProductionReportAdvanced,
            );
          }
          break;
        case "packaging":
          const packagingReport = await reportsApi.getPackagingReport(
            values.from,
            values.to,
          );

          if (grower.Workflow === "simple") {
            setPackagingReportSimple(packagingReport as PackagingReportSimple);
          } else {
            setPackagingReportAdvanced(
              packagingReport as PackagingReportAdvanced,
            );
          }
          break;
        default:
          throw new Error("invalid report");
      }

      mixpanel.track("Generate Report", {
        type: values.report,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const onClickCancelUpgradePlan = () => {
    history.replace("/home");
  };
  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);

  return (
    <Page title={getCustomLabel(grower ?? null, "reports")}>
      {" "}
      <Formik
        initialValues={{
          report: "sales",
          from: null,
          to: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
      >
        {(formProps) => (
          <Form noValidate>
            <Box>
              <FastField name="report">
                {(fieldProps: FieldProps) => (
                  <AppTextField {...fieldProps} autoFocus select label="Report">
                    <MenuItem key="sales" value="sales">
                      Sales
                    </MenuItem>
                    <MenuItem key="crop-production" value="crop-production">
                      Crop Production
                    </MenuItem>
                    <MenuItem key="packaging" value="packaging">
                      Packaging
                    </MenuItem>
                    <MenuItem key="orders-summary" value="orders-summary">
                      Orders Summary
                    </MenuItem>
                  </AppTextField>
                )}
              </FastField>
              <Field name="from">
                {(fieldProps: FieldProps) => (
                  <Box mt={2}>
                    <MobileDatePicker
                      value={fieldProps.field.value}
                      onChange={(date) =>
                        fieldProps.form.setFieldValue("from", date)
                      }
                      format="MMM d, y"
                      label="From"
                      closeOnSelect={true}
                      open={openFrom}
                      onOpen={() => setOpenFrom(true)}
                      onClose={() => setOpenFrom(false)}
                      slots={{
                        openPickerIcon: () => null,
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          name: fieldProps.field.name,
                          error:
                            !!fieldProps.meta.error && fieldProps.meta.touched,
                          helperText:
                            fieldProps.meta.touched && fieldProps.meta.error,
                          onClick: () => setOpenFrom(true),
                          onKeyDown: (e) => e.preventDefault(),

                          inputProps: {
                            readOnly: true,
                          },
                        },
                        toolbar: { hidden: true },
                      }}
                    />
                  </Box>
                )}
              </Field>
              <Field name="to">
                {(fieldProps: FieldProps) => (
                  <Box mt={2}>
                    <MobileDatePicker
                      value={fieldProps.field.value}
                      onChange={(date) =>
                        fieldProps.form.setFieldValue("to", date)
                      }
                      format="MMM d, y"
                      label="To"
                      closeOnSelect={true}
                      open={openTo}
                      onOpen={() => setOpenTo(true)}
                      onClose={() => setOpenTo(false)}
                      slots={{
                        openPickerIcon: () => null,
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          name: fieldProps.field.name,
                          error:
                            !!fieldProps.meta.error && fieldProps.meta.touched,
                          helperText:
                            fieldProps.meta.touched && fieldProps.meta.error,
                          onClick: () => setOpenTo(true),
                          onKeyDown: (e) => e.preventDefault(),
                          inputProps: {
                            readOnly: true,
                          },
                        },
                        toolbar: { hidden: true },
                      }}
                    />
                  </Box>
                )}
              </Field>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={busy}
                >
                  Generate
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      {packagingReportSimple &&
        (packagingReportSimple.Rows.length > 0 ? (
          <Box sx={{ mt: 2, height: 600, width: "100%" }}>
            <StripedDataGrid
              storageKey="reports-packaging-simple-grid"
              columns={packagingReportSimpleColumns}
              rows={packagingReportSimple.Rows.map((row, index) => ({
                id: index,
                ...row,
                HarvestDateFormatted: row.HarvestDate
                  ? DateTime.fromISO(row.HarvestDate).toFormat("MMM d, yyyy")
                  : "",
              }))}
              showToolbar
              autoPageSize
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No packaging between these dates
            </Typography>
          </Box>
        ))}
      {packagingReportAdvanced &&
        (packagingReportAdvanced.Rows.length > 0 ? (
          <StripedDataGrid
            storageKey="reports-packaging-advanced-grid"
            sx={{ mt: 2 }}
            columns={packagingReportAdvancedColumns}
            rows={packagingReportAdvanced.Rows.map((c) => ({
              id: v4(),
              ...c,

              DateExpectedFormatted: c.DateExpected
                ? DateTime.fromISO(c.DateExpected).toFormat("MMM d, yyyy")
                : "",

              DateActualFormatted: c.DateActual
                ? DateTime.fromISO(c.DateActual).toFormat("MMM d, yyyy")
                : "",

              HarvestDateFormatted: c.HarvestDate
                ? DateTime.fromISO(c.HarvestDate).toFormat("MMM d, yyyy")
                : "",
            }))}
            initialState={{
              filter: undefined,
              sorting: {
                sortModel: [
                  {
                    field: "HarvestDate",
                    sort: "asc",
                  },
                ],
              },
            }}
            showToolbar
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                csvOptions: { allColumns: true },
              },
            }}
            getRowClassName={(params: GridRowClassNameParams) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            disableRowSelectionOnClick
          />
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No packaging between these dates
            </Typography>
          </Box>
        ))}
      {cropProductionReportSimple &&
        (cropProductionReportSimple.ReportData.length > 0 ? (
          <StripedDataGrid
            storageKey="reports-crop-production-simple-grid"
            sx={{ mt: 2 }}
            columns={cropProductionReportSimpleColumns}
            rows={cropProductionReportSimple.ReportData.map((c) => ({
              id: c.Crop + c.HarvestDate,

              ...c,

              SowingDateFormatted: DateTime.fromISO(c.SowingDate).toFormat(
                "MMM d, yyyy",
              ),

              HarvestDateFormatted: c.HarvestDate
                ? DateTime.fromISO(c.HarvestDate).toFormat("MMM d, yyyy")
                : "",
            }))}
            initialState={{
              filter: undefined,
              sorting: {
                sortModel: [
                  {
                    field: "SowingDate",
                    sort: "asc",
                  },
                ],
              },
            }}
            showToolbar
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                csvOptions: {
                  allColumns: true,
                  fileName: `crop-production-report-${DateTime.now().toISODate()}.csv`,
                },
              },
            }}
            getRowClassName={(params: GridRowClassNameParams) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            disableRowSelectionOnClick
          />
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No crop production between these dates
            </Typography>
          </Box>
        ))}
      {cropProductionReportAdvanced &&
        (cropProductionReportAdvanced.ReportData.length > 0 ? (
          <StripedDataGrid
            storageKey="reports-crop-production-advanced-grid"
            sx={{ mt: 2 }}
            columns={cropProductionReportAdvancedColumns}
            rows={cropProductionReportAdvanced.ReportData.map((c) => {
              const seedLotsDisplay =
                c.SoakedSeedLotNumbers ||
                c.SowedSeedLotNumbers ||
                c.SeedLots ||
                "";

              const seedLotNotesDisplay =
                c.SoakedSeedLotNotes ||
                c.SowedSeedLotNotes ||
                c.SeedLotNotes ||
                "";

              const n = Number(c.HarvestedBufferExpected);
              const harvestedBufferExpectedDisplay = Number.isFinite(n)
                ? String(Math.round(n))
                : "";

              return {
                id: v4(),
                ...c,

                // ✅ computed display fields (no DataGrid getter calls)
                SeedLotsDisplay: seedLotsDisplay,
                SeedLotNotesDisplay: seedLotNotesDisplay,
                HarvestedBufferExpectedDisplay: harvestedBufferExpectedDisplay,

                // keep your formatted dates
                SowedDateActualFormatted: c.SowedDateActual
                  ? DateTime.fromISO(c.SowedDateActual).toFormat("MMM d, yyyy")
                  : "",
                SowedDateExpectedFormatted: c.SowedDateExpected
                  ? DateTime.fromISO(c.SowedDateExpected).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                HarvestedDateExpectedFormatted: c.HarvestedDateExpected
                  ? DateTime.fromISO(c.HarvestedDateExpected).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                HarvestedDateActualFormatted: c.HarvestedDateActual
                  ? DateTime.fromISO(c.HarvestedDateActual).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                SoakedDateExpectedFormatted: c.SoakedDateExpected
                  ? DateTime.fromISO(c.SoakedDateExpected).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                SoakedDateActualFormatted: c.SoakedDateActual
                  ? DateTime.fromISO(c.SoakedDateActual).toFormat("MMM d, yyyy")
                  : "",
                BlackoutDateExpectedFormatted: c.BlackoutDateExpected
                  ? DateTime.fromISO(c.BlackoutDateExpected).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                BlackoutDateActualFormatted: c.BlackoutDateActual
                  ? DateTime.fromISO(c.BlackoutDateActual).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                UncoveredDateExpectedFormatted: c.UncoveredDateExpected
                  ? DateTime.fromISO(c.UncoveredDateExpected).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
                UncoveredDateActualFormatted: c.UncoveredDateActual
                  ? DateTime.fromISO(c.UncoveredDateActual).toFormat(
                      "MMM d, yyyy",
                    )
                  : "",
              };
            })}
            initialState={{
              filter: undefined,
              sorting: {
                sortModel: [
                  {
                    field: "SowedDateExpected",
                    sort: "asc",
                  },
                ],
              },
              columns: {
                columnVisibilityModel: {
                  SoakedDateActual: false,
                  SoakedDateExpected: false,
                  SoakedBy: false,
                  SoakedNotes: false,
                  SoakedTraysExpected: false,
                  SoakedTraysActual: false,
                  SowedBy: false,
                  SowedNotes: false,
                  SowedTraysExpected: false,
                  SowedTraysActual: false,
                  SowedCropSowingRate: false,
                  SowedCropExpectedYield: false,
                  SowedCropNotes: false,
                  SowedSeedNotes: false,
                  SeedLotNotes: false,
                  BlackoutDateExpected: false,
                  BlackoutDateActual: false,
                  BlackoutBy: false,
                  BlackoutNotes: false,
                  BlackoutTraysExpected: false,
                  BlackoutTraysActual: false,
                  UncoveredDateExpected: false,
                  UncoveredDateActual: false,
                  UncoveredBy: false,
                  UncoveredNotes: false,
                  UncoveredTraysExpected: false,
                  UncoveredTraysActual: false,
                  HarvestedBy: false,
                  HarvestedNotes: false,
                  HarvestedTraysExpected: false,
                  HarvestedTraysActual: false,
                  HarvestedYieldExpected: false,
                  HarvestedYieldActual: false,
                },
              },
            }}
            showToolbar
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                csvOptions: {
                  allColumns: true,
                  fileName: `crop-production-report-${DateTime.now().toISODate()}.csv`,
                },
              },
            }}
            getRowClassName={(params: GridRowClassNameParams) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            disableRowSelectionOnClick
          />
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No crop production between these dates
            </Typography>
          </Box>
        ))}
      {selectedReport === "sales" &&
        (salesReport && salesReport.length > 0 ? (
          <StripedDataGrid
            storageKey="reports-sales-grid"
            columns={salesReportColumns}
            rows={salesReport.map((c) => ({
              id: v4(),
              ...c,
              HarvestDateFormatted: DateTime.fromISO(c.HarvestDate).toFormat(
                "MMM d, yyyy",
              ),
              PriceFormatted: formatCurrency(c.Price, grower.CountryISO),
              TotalFormatted: formatCurrency(c.Total, grower.CountryISO),
            }))}
            showToolbar
            slotProps={{
              toolbar: {
                csvOptions: {
                  allColumns: true,
                  fileName: `sales-report-${DateTime.now().toISODate()}`,
                },
              },
            }}
          />
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No sales between these dates
            </Typography>
          </Box>
        ))}
      {ordersSummaryReport &&
        sizes &&
        (ordersSummaryReport.ProductSales.length > 0 ? (
          <Box mt={2}>
            <TableContainer
              component={Paper}
              sx={{ border: "2px solid black" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      rowSpan={4}
                      sx={{
                        backgroundColor: "#d9d9d9",
                        position: "sticky",
                        left: 0,
                        borderBottom: "0.5px solid black",
                        borderLeft: "0.5px solid black",
                      }}
                    >
                      Customers
                    </TableCell>
                    <TableCell
                      align="center"
                      rowSpan={4}
                      sx={{
                        backgroundColor: "#d9d9d9",
                        borderBottom: "0.5px solid black",
                        borderLeft: "0.5px solid black",
                      }}
                    >
                      Customer Type
                    </TableCell>
                    <TableCell
                      align="center"
                      rowSpan={3}
                      valign="bottom"
                      sx={{
                        backgroundColor: "#d9d9d9",
                        borderBottom: "0.5px solid black",
                        borderLeft: "0.5px solid black",
                      }}
                    >
                      Total Sales
                    </TableCell>
                    {ordersSummaryReport.ProductSales.sort(sort).map((v) => (
                      <TableCell
                        key={`header-${v.ID}`}
                        align="center"
                        colSpan={4}
                        sx={{
                          backgroundColor: "#f6b26b",
                          borderBottom: "0.5px solid black",
                          borderLeft: "2px solid black",
                        }}
                      >
                        {v.Name}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    {ordersSummaryReport.ProductSales.sort(sort).map((v) => (
                      <TableCell
                        key={`totals-${v.ID}`}
                        align="center"
                        colSpan={4}
                        sx={{
                          backgroundColor: "#f6b26b",
                          borderBottom: "0.5px solid black",
                          borderLeft: "2px solid black",
                        }}
                      >
                        {formatCurrency(v.TotalSales, countryISO)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    {ordersSummaryReport.ProductSales.sort(sort).map((p) => (
                      <Fragment key={`sizes-${p.ID}`}>
                        {sizes.map((s, i) => (
                          <TableCell
                            key={`size-${p.ID}-${s.id}`}
                            align="center"
                            sx={{
                              backgroundColor:
                                i === 0
                                  ? "#a4c2f4"
                                  : i === 1
                                    ? "#ffe599"
                                    : "#f9cb9c",
                              borderBottom: "0.5px solid black",
                              ...(i === 0 && {
                                borderLeft: "2px solid black",
                              }),
                            }}
                          >
                            {formatCurrency(
                              ordersSummaryReport.ProductSizeSales.find(
                                (pss) => pss.ID === p.ID && pss.SizeID === s.id,
                              )?.TotalSales || 0,
                              countryISO,
                            )}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: "#b4a7d6",
                            borderBottom: "0.5px solid black",
                          }}
                        >
                          {formatCurrency(
                            ordersSummaryReport.ProductSizeSales.find(
                              (pss) =>
                                pss.ID === p.ID && pss.SizeType === "tray",
                            )?.TotalSales || 0,
                            countryISO,
                          )}
                        </TableCell>
                      </Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "#f6b26b",
                        borderBottom: "0.5px solid black",
                      }}
                    >
                      {formatCurrency(
                        ordersSummaryReport.TotalSales,
                        countryISO,
                      )}
                    </TableCell>
                    {ordersSummaryReport.ProductSales.sort(sort).map((p) => (
                      <Fragment key={`labels-${p.ID}`}>
                        {sizes.map((s, i) => (
                          <TableCell
                            key={`label-${p.ID}-${s.id}`}
                            align="center"
                            sx={{
                              backgroundColor:
                                i === 0
                                  ? "#a4c2f4"
                                  : i === 1
                                    ? "#ffe599"
                                    : "#f9cb9c",
                              borderBottom: "0.5px solid black",
                              ...(i === 0 && {
                                borderLeft: "2px solid black",
                              }),
                            }}
                          >
                            {s.name.charAt(0).toUpperCase()}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: "#b4a7d6",
                            borderBottom: "0.5px solid black",
                          }}
                        >
                          T
                        </TableCell>
                      </Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersSummaryReport.CustomerSales.sort(sort).map((v) => (
                    <TableRow key={`row-${v.ID}`}>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f9cb9c",
                          borderBottom: "0.5px solid black",
                          position: "sticky",
                          left: 0,
                        }}
                      >
                        {v.Name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f9cb9c",
                          borderBottom: "0.5px solid black",
                        }}
                      >
                        {v.Type}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "#f9cb9c",
                          borderBottom: "0.5px solid black",
                        }}
                      >
                        {formatCurrency(v.TotalSales, countryISO)}
                      </TableCell>
                      {ordersSummaryReport.ProductSales.sort(sort).map((ps) => (
                        <Fragment key={`data-${v.ID}-${ps.ID}`}>
                          {sizes.map((s, i) => {
                            const sales =
                              ordersSummaryReport.CustomerProductSizeSales.find(
                                (cpss) =>
                                  cpss.ID === v.ID &&
                                  cpss.ProductID === ps.ID &&
                                  cpss.SizeID === s.id,
                              )?.TotalSales || 0;

                            return (
                              <TableCell
                                key={`sales-${v.ID}-${ps.ID}-${s.id}`}
                                align="center"
                                sx={{
                                  backgroundColor:
                                    sales !== 0
                                      ? i === 0
                                        ? "#a4c2f4"
                                        : i === 1
                                          ? "#ffe599"
                                          : "#f9cb9c"
                                      : undefined,
                                  borderBottom: "0.5px solid black",
                                  ...(i === 0 && {
                                    borderLeft: "2px solid black",
                                  }),
                                }}
                              >
                                {formatCurrency(sales, countryISO)}
                              </TableCell>
                            );
                          })}
                          <TableCell
                            align="center"
                            sx={{
                              backgroundColor:
                                ordersSummaryReport.CustomerProductSizeSales.find(
                                  (cpss) =>
                                    cpss.ID === v.ID &&
                                    cpss.ProductID === ps.ID &&
                                    cpss.SizeType === "tray",
                                )?.TotalSales
                                  ? "#b4a7d6"
                                  : undefined,
                              borderBottom: "0.5px solid black",
                            }}
                          >
                            {formatCurrency(
                              ordersSummaryReport.CustomerProductSizeSales.find(
                                (cpss) =>
                                  cpss.ID === v.ID &&
                                  cpss.ProductID === ps.ID &&
                                  cpss.SizeType === "tray",
                              )?.TotalSales || 0,
                              countryISO,
                            )}
                          </TableCell>
                        </Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Box mt={2}>
            <Typography variant="body1">
              No orders between these dates
            </Typography>
          </Box>
        ))}
      <LoadingBackdrop open={busy} />
      <UpgradePlanDialog
        open={openUpgradePlanDialog}
        onClickCancel={onClickCancelUpgradePlan}
      />
    </Page>
  );
}

function sort(a: { Name: string }, b: { Name: string }): number {
  return a.Name.localeCompare(b.Name);
}
